import _ from 'underscore'
import URI from 'urijs'
import Backbone from '@polleverywhere/backbone'
import InstanceMixins, { Common } from './mixins/_instance_common'
import Archives from './archive_collection'
import Comparisons from './comparison_collection'

/**
 * Perform some operations whenever activity is re-hydrated.
 * @param {Backbone.Model|Collection} modelOrCollection
 * @param {Object} response
 * @param {Object} options Options passed into `save|fetch|etc`
 */
function handleActivitySync (modelOrCollection, response, options = {}) {
  // For MCPs, `parse` accepts a `resetOptions` flag to skip parsing options
  // data. Only when this flag is explicitly `false` do we know options data
  // is being skipped. Thus, only clear current archive otherwise.
  if (options.resetOptions !== false) {
    setCurrentArchive.call(this)
  }
}

/**
 * Handle whenever archive results data is fetched.
 * @param {Backbone.Model.Archive|Collection.Archives} model
 * @param {Object} data Response data
 * @param {Object} options When `silent` is true, do nothing.
 */
function handleArchiveData (model, data, options = {}) {
  // When syncing individual archives inside the collection, passing silent does
  // not suppress the sync event from triggering for some reason. So do it here.
  if (options.silent) { return }

  // Ignore fetches occurring against the collection. We only need to parse data
  // when an individual archived result is fetched.
  if (model instanceof Archives) { return }

  const pollData = {}
  if (model.has('options')) {
    // For MCPs
    pollData.options = model.get('options')
  }
  if (model.has('results')) {
    // TODO: remove this when `clicks` is deprecated from the normalizer in instance_common.js #166968428
    const key = this.activity.type() === 'clickable_image' ? 'clicks' : 'results'
    pollData[key] = model.get('results')
  }

  this.parse(pollData, options)

  // Keep record of which archive is currently loaded.
  setCurrentArchive.call(this, model)
}

/**
 * Handle whenever comparison(s) data is fetched.
 * @param {Comparisons|Comparison} model Comparison collection or model.
 * @param {Object} data Response data
 */
function handleComparisonData (model, data = {}) {
  // Disregard fetches occurring against the collection. We only need to parse
  // data when an individual comparison report is fetched.
  if (model instanceof Comparisons) { return }
  this.parse(data)
}

/**
 * Update internal property to keep record of currently loaded archive.
 * @param {Archive} archive
 */
function setCurrentArchive (archive = null) {
  this._currentArchive = archive
}

/**
 * Generates the Firehose channel path for instance data.
 * @param {string} path Additional path to append to the base URL.
 * @return {string} Always suffixed with a `.json` extension
 */
function getInstanceChannelPath (path = '') {
  const channel = URI('/instances')
    .segment(this.activity.type() + 's')
    .segment(this.activity.get('presenter_key'))
  if (path) {
    channel.segment(path)
  }
  return channel.suffix('json').toString()
}

/**
 * Generates the Firehose channel path for activity data.
 * @param {string} path Additional path to append to the base URL.
 * @return {string} Always suffixed with a `.json` extension
 */
function getActivityChannelPath () {
  return URI('/')
    .segment(this.activity.type() + 's')
    .segment(this.activity.get('presenter_shared_key'))
    .segment('presenter_shared')
    .suffix('json')
    .toString()
}

export default class PresenterInstance extends Backbone.Model {
  initialize (attrs = {}, options = {}) {
    _.extend(this, InstanceMixins(this))

    /**
     * Contains reference to the internal activity model.
     * @type {Backbone.Model}
     */
    this.activity = options.activity

    // Parse the activity's attributes so it can be normalized.
    this.parse(this.activity.attributes)
  }

  get idAttribute () {
    return Common.getIdAttribute.call(this)
  }

  get urlRoot () {
    return Common.getUrlRoot.call(this)
  }

  /**
   * Generates a URL path to the shareable response route on mobile where
   * participants can respond even while the activity is not activated.
   * This route must match a valid route on mobile.
   * @return {string} Format => `/:plural_type/:participant_key/respond`
   */
  get webRespondUrl () {
    const key =
      this.activity.get('participant_key') || this.activity.get('shared_key')
    if (!key) {
      throw new Error(`${this.type}: webRespondUrl requires a key`)
    }
    return URI('/')
      .segment(this.activity.type() + 's')
      .segment(key)
      .segment('respond')
      .toString()
  }

  /**
   * Read-only property indicating the instance type, which is used to determine
   * the correct resource paths, keys, etc.
   * @return {string}
   */
  get type () {
    return 'presenter'
  }

  /**
   * Returns the list of Firehose channels this instance subscribes to:
   * => `/:plural_activity_type/:presenter_shared_key/presenter_shared.json`
   * => `/instances/:plural_activity_type/:presenter_key.json`
   * => `/instances/:plural_activity_type/:presenter_key/results.json`
   * @return {string[]}
   */
  get channels () {
    Common.requireAttrs(this.activity.attributes, [
      'presenter_shared_key',
      'presenter_key'
    ])

    const instanceChannel = getInstanceChannelPath.call(this)
    const resultChannel = getInstanceChannelPath.call(this, 'results')
    const activityChannel = getActivityChannelPath.call(this)

    return [
      instanceChannel,
      activityChannel,
      resultChannel
    ]
  }

  /**
   * Read-only collection of archived results. Consumers can use this property
   * to fetch a list of archives and/or an individual archived result.
   * @type {Backbone.Collection}
   */
  get archives () {
    if (!this._archives || this._archives.activity !== this.activity) {
      this._archives = new Archives([], { activity: this.activity })

      // Update activity results whenever fetching a single archive.
      this.listenTo(this._archives, 'sync', handleArchiveData)

      // To keep track of current archive, track when activity is re-hydrated.
      this.listenTo(this, 'sync', handleActivitySync)
      this.listenTo(this.activity, 'sync', handleActivitySync)
    }

    return this._archives
  }

  /**
   * Read-only collection of comparisons, available only for MCPs. Consumers can
   * use this to fetch a list available polls to get comparison data for.
   * @type {Backbone.Collection}
   */
  get comparisons () {
    if (/multiple/.test(this.activity.type()) &&
      (!this._comparisons || this._comparisons.activity !== this.activity)) {
      this._comparisons = new Comparisons([], { activity: this.activity })

      // Update activity results whenever fetching a single archive.
      this.listenTo(this._comparisons, 'sync', handleComparisonData)
    }

    return this._comparisons
  }

  /**
   * Read-only property consumers can use to determine if this activity is
   * loaded with an archived result. Returns `null` when using latest.
   * @return {Archive}
   */
  get currentArchive () {
    return this._currentArchive
  }

  parse (data = {}, options = {}) {
    Common.parseActivity.call(this, data, options)

    // Keep this model free of attributes.
    return {}
  }
}
